import { tenantTimezoneAtom } from "@/atoms/tenant-clock"
import { Button, Card, Heading, Text } from "@/components/ui"
import type { WonPrize } from "@/domain/marketing/api"
import { prizeItemWords, prizeOfferLabel, type PrizeOffer } from "@/domain/marketing/prize-offer"
import { prizeExpired, prizeOrigin, prizeValidity } from "@/domain/marketing/won-prize"
import { useAuthStatus } from "@/hooks/use-auth-status"
import { useReceivedCoupon } from "@/hooks/use-coupon-readiness"
import { usePrizeOffer, useRedeemPrize } from "@/hooks/use-redeem-prize"
import { useWonPrizes } from "@/hooks/use-won-prizes"
import type { CouponBenefit } from "@/lib/coupon-benefit"
import { prizeNotUsableMessage } from "@/lib/coupon-readiness"
import { useAtomValue } from "jotai"
import { useId, useState } from "react"
import { PrizeArt } from "./PrizeArt"
import { PrizeChoiceList } from "./prize-redeem/PrizeChoiceList"
import { PrizeCodeDisclosure } from "./prize-redeem/PrizeCodeDisclosure"

const ORDER_BENEFIT: CouponBenefit = { kind: "order" }

function PrizeAction({ code, offer }: { code: string; offer: PrizeOffer }) {
  const redeem = useRedeemPrize()
  const [picking, setPicking] = useState(false)
  if (offer.kind === "pick" && picking) {
    return <PrizeChoiceList tone="store" choices={offer.choices} onChoose={(choice) => redeem(code, { kind: "item", choice })} />
  }
  function act() {
    if (offer.kind === "pick") setPicking(true)
    else if (offer.kind === "add") redeem(code, { kind: "item", choice: offer.choice })
    else redeem(code, { kind: offer.kind })
  }
  return (
    <Button size="md" fullWidth onClick={act}>
      {prizeOfferLabel(offer)}
    </Button>
  )
}

function PrizeRow({ prize }: { prize: WonPrize }) {
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const received = useReceivedCoupon(prize.code || null)
  const readiness = received?.readiness
  const checked = readiness !== undefined && readiness.status !== "checking"
  const offer = usePrizeOffer(received?.details?.benefit ?? (checked ? ORDER_BENEFIT : null))
  if (readiness?.status === "invalid") return null
  const itemWords = prizeItemWords(received?.terms)
  const usable = readiness?.status === "usable" || readiness?.status === "unknown"
  const note = !prize.code
    ? "Preparando seu prêmio…"
    : readiness && readiness.status !== "checking" && !usable
      ? prizeNotUsableMessage(readiness)
      : null
  const validity = prizeValidity(prize.expiresAt, timeZone)
  return (
    <Card as="li" variant="outline" padding="sm" className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <PrizeArt artKey={prize.prizeArtKey} label={prize.prizeLabel} size={44} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Text size="xs" weight="semibold" tone="muted">
            {prizeOrigin(prize.gameType)}
          </Text>
          <Text weight="bold" tone="default">
            {itemWords?.headline ?? (prize.prizeLabel || "Seu prêmio")}
          </Text>
          {itemWords?.detail ? (
            <Text size="xs" tone="muted">
              {itemWords.detail}
            </Text>
          ) : null}
          {validity ? (
            <Text size="xs" tone="subtle">
              {validity}
            </Text>
          ) : null}
        </div>
      </div>
      {note ? (
        <Text size="sm" tone="muted">
          {note}
        </Text>
      ) : null}
      {prize.code && usable && offer ? <PrizeAction code={prize.code} offer={offer} /> : null}
      {prize.code && readiness?.status === "checking" ? (
        <Text size="sm" tone="muted">
          Conferindo seu prêmio…
        </Text>
      ) : null}
      {prize.code ? <PrizeCodeDisclosure code={prize.code} tone="store" /> : null}
    </Card>
  )
}

/** "Meus prêmios" in Minha conta: the prizes won at this store that can still be used, with their validity and the same way to spend them as the game; nothing when there are none. */
export function MyPrizesCard() {
  const titleId = useId()
  const authenticated = useAuthStatus()
  const won = useWonPrizes(authenticated)
  const now = new Date()
  const prizes = won.status === "ready" ? won.prizes.filter((prize) => !prizeExpired(prize, now)) : []
  if (prizes.length === 0) return null
  return (
    <Card as="section" padding="lg" aria-labelledby={titleId} className="flex flex-col gap-3 [&:has(ul:empty)]:hidden">
      <div className="flex flex-col gap-1">
        <Heading id={titleId} level={2} size="card">
          Meus prêmios
        </Heading>
        <Text size="sm" tone="muted">
          O que você ganhou na roleta e na raspadinha e ainda pode usar.
        </Text>
      </div>
      <ul className="flex flex-col gap-3">
        {prizes.map((prize) => (
          <PrizeRow key={prize.drawId} prize={prize} />
        ))}
      </ul>
    </Card>
  )
}
